import { useMemo } from "react";
import { useUser } from "@clerk/clerk-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useApiAuth } from "./useApiAuth";
import { apiUrl } from "../lib/api";
import { queryKeys } from "../lib/queryKeys";

export interface HomeSummaryPoolEntry {
  pool_code: string;
  pool_name: string;
  pool_status: "PRE" | "ACTIVE" | "COMPLETED";
  membership_status: "PENDING" | "ACTIVE" | "REJECTED" | "DISABLED";
  is_admin: boolean;
  competition_code: string;
  competition_name: string;
  season_code: string;
  points_balance: number;
  total_points: number;
  open_orders_count: number;
  momentum: {
    rank_now: number | null;
    rank_24h_ago: number | null;
    rank_delta_24h: number;
    pnl_delta_24h: number;
  };
  unread_events: {
    total: number;
    fills: number;
    outbid_alerts: number;
    rank_changes: number;
  };
  conviction_opportunity: {
    count: number;
    best_team_code: string | null;
    best_side?: "BUY" | "SELL" | null;
    best_edge_points: number;
    top_deals?: Array<{
      side: "BUY" | "SELL";
      team_code: string;
      team_primary_color: string | null;
      team_secondary_color: string | null;
      edge_points: number;
      market_ref: number;
      available_qty: number;
      projected_total: number;
    }>;
  };
  competitive_snapshot: {
    leader: {
      trader_code: string | null;
      points: number | null;
    };
    top_climber_24h: {
      trader_code: string | null;
      rank_delta_24h: number;
    };
  };
  cta: {
    primary: {
      id: string;
      label: string;
      reason?: string | null;
      action: {
        tab: "market" | "portfolio" | "simulation";
        pool_code: string;
        team_code: string | null;
        side?: "BUY" | "SELL" | null;
      };
    };
  };
}

type HomeSummaryResponse = {
  generated_at: string;
  pools: HomeSummaryPoolEntry[];
};

type UseHomePoolSummaryArgs = {
  enabled?: boolean;
};

const queryKey = queryKeys.homeSummary();

export function useHomePoolSummary({ enabled = true }: UseHomePoolSummaryArgs = {}) {
  const { acquireToken } = useApiAuth();
  const { isSignedIn: isAuthenticated } = useUser();
  const canLoad = enabled && isAuthenticated;

  const query = useQuery({
    queryKey,
    enabled: canLoad,
    queryFn: async () => {
      const accessToken = await acquireToken();
      if (!accessToken) {
        throw new Error("Authentication required to load home summary.");
      }

      const response = await fetch(apiUrl("/api/pools/home-summary"), {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load home summary (${response.status})`);
      }

      const payload: HomeSummaryResponse = await response.json();
      return payload;
    },
  });

  const markReadMutation = useMutation({
    mutationFn: async ({ poolCode, lastSeenRank }: { poolCode: string; lastSeenRank?: number | null }) => {
      if (!enabled || !isAuthenticated) {
        return;
      }

      const accessToken = await acquireToken();
      if (!accessToken) {
        return;
      }

      await fetch(apiUrl(`/api/pools/${encodeURIComponent(poolCode)}/home-summary/mark-read`), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ lastSeenRank: lastSeenRank ?? null }),
      });
    },
    onError: (error) => {
      console.debug("Failed to mark home summary as read", error);
    },
  });

  const summary = canLoad ? query.data ?? null : null;

  return {
    summary,
    byPoolCode: useMemo(
      () => new Map((summary?.pools ?? []).map((pool) => [pool.pool_code, pool])),
      [summary],
    ),
    isLoading: canLoad ? query.isLoading : false,
    isError: canLoad ? query.isError : false,
    reload: query.refetch,
    markRead: async (poolCode: string, lastSeenRank?: number | null) => {
      await markReadMutation.mutateAsync({ poolCode, lastSeenRank });
    },
  };
}
